import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from "recharts";

export default function Reports() {
  const [trend, setTrend] = useState([]);
  const [machines, setMachines] = useState([]);
  const [downtimes, setDowntimes] = useState([]);
  const [entries, setEntries] = useState([]);

  const load = async () => {
    const [t, m, d, e] = await Promise.all([
      api.get("/dashboard/production-trend?days=14"),
      api.get("/machines/"),
      api.get("/downtimes/"),
      api.get("/production-entries/"),
    ]);
    setTrend(t.data); setMachines(m.data); setDowntimes(d.data); setEntries(e.data);
  };
  useEffect(() => { load(); }, []);

  // Machine wise output
  const byMachine = machines.map(m => {
    const total = entries.filter(e => e.machine_id === m.id).reduce((a, b) => a + b.produced_meter, 0);
    return { code: m.code, produced: Math.round(total) };
  }).sort((a, b) => b.produced - a.produced).slice(0, 12);

  // Downtime by category
  const CATS = ["Mechanical", "Electrical", "Quality", "Material", "Operator", "Power", "Maintenance", "Other"];
  const byCat = CATS.map(c => ({ cat: c, minutes: downtimes.filter(d => d.reason_category === c).reduce((a, b) => a + b.duration_min, 0) })).filter(c => c.minutes > 0);
  const COLORS = ["#EF4444", "#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#F97316", "#EC4899", "#6B7280"];

  // Shift-wise
  const shifts = ["A", "B", "C"];
  const byShift = shifts.map(s => ({ shift: `Shift ${s}`, produced: entries.filter(e => e.shift === s).reduce((a, b) => a + b.produced_meter, 0) }));

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="overline mb-1">Insights</div>
        <h1 className="font-display text-3xl font-black tracking-tight text-white">Reports & Analytics</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="tactical-card p-5">
          <div className="overline">Trend</div>
          <div className="font-display text-lg font-bold text-white mb-4">14-day Production</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trend}>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#71717A" fontSize={10} />
              <YAxis stroke="#71717A" fontSize={10} />
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A", borderRadius: 4 }} />
              <Legend />
              <Line type="monotone" dataKey="produced" name="Produced" stroke="#007AFF" strokeWidth={2} />
              <Line type="monotone" dataKey="rejected" name="Rejected" stroke="#EF4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="tactical-card p-5">
          <div className="overline">Efficiency</div>
          <div className="font-display text-lg font-bold text-white mb-4">Top Machines by Output</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byMachine}>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
              <XAxis dataKey="code" stroke="#71717A" fontSize={10} />
              <YAxis stroke="#71717A" fontSize={10} />
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A", borderRadius: 4 }} />
              <Bar dataKey="produced" fill="#007AFF" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="tactical-card p-5">
          <div className="overline">Loss Analysis</div>
          <div className="font-display text-lg font-bold text-white mb-4">Downtime by Category (min)</div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={byCat} innerRadius={50} outerRadius={100} dataKey="minutes" nameKey="cat" paddingAngle={2}>
                {byCat.map((entry, i) => <Cell key={entry.cat || `cat-${i}`} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A", borderRadius: 4 }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="tactical-card p-5">
          <div className="overline">Shift Analysis</div>
          <div className="font-display text-lg font-bold text-white mb-4">Production by Shift</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byShift}>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
              <XAxis dataKey="shift" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" fontSize={10} />
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A", borderRadius: 4 }} />
              <Bar dataKey="produced" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
